import LocationService from '@/services/LocationService'
import DictionariesService from '@/services/DictionariesService'
import Tariff from '@/dto/deliveries/Tariff'
import '@/services/hot/AutoCompleteObjectsEditor'
import Handsontable from 'handsontable'
import GridSettings = Handsontable.GridSettings
import ColumnSettings = Handsontable.ColumnSettings
import { AutofillPlaces } from '@/helpers/AutofillPlaces'
import { AutofillCountries } from '@/helpers/AutofillCountries'
import CellProperties = Handsontable.CellProperties

export default {
  getZoneItemsSettings(): GridSettings {
    return {
      licenseKey: 'non-commercial-and-evaluation',
      colHeaders: true,
      nestedHeaders: [
        [
          '',
          '',
          '',
          { label: 'Сроки доставки, дни', colspan: 2 },
          '',
          { label: 'Максимальные размеры, см', colspan: 3 },
          '',
        ],
        [
          'Страна',
          'Край, область',
          'Населенный пункт',
          'от',
          'до',
          `Макс. вес, гр`,
          'высота',
          'ширина',
          'длина',
          '',
        ],
      ],
      afterPaste(this: Handsontable, ...args) {
        AutofillPlaces(this, ...args)
        AutofillCountries(this, ...args)
      },
      afterChange(this: Handsontable, changes, source) {
        if (!changes || changes.length > 1 || source !== 'edit') {
          return undefined
        }

        const [row, prop, , newValue] = changes[0]

        if (prop === 'city.region' && newValue) {
          const region = newValue
          const russia = LocationService.getRussia()

          this.setDataAtRowProp(row, 'country.id', russia.id, 'load')
          this.setDataAtRowProp(row, 'country.name', russia.name, 'load')

          if (region) {
            this.setDataAtRowProp(row, 'state.id', region.id, 'load')
            this.setDataAtRowProp(row, 'state.name', region.name, 'load')
          }
        }

        if (prop === 'state.id' && newValue) {
          const placeName = this.getDataAtRowProp(row, 'city.name')
          const regionMeta = this.getCellMeta(row, 1)

          if (!regionMeta.valid) {
            regionMeta.valid = true

            this.render()
          }

          LocationService.getPlacesForRegion(newValue, placeName).then(
            places => {
              if (places.length === 1) {
                const place = places[0]
                this.setDataAtRowProp(row, 'city.id', place.id, 'load')
                this.setDataAtRowProp(row, 'city.name', place.name, 'load')

                const cityMeta = this.getCellMeta(row, 2)

                if (!cityMeta.valid) {
                  cityMeta.valid = true

                  this.render()
                }
              }
            }
          )
        }
      },
      columns: [
        {
          data: 'country.name',
          strict: true,
          type: 'autocomplete-objects',
          source(query: string, process: Function) {
            LocationService.getCountries().then(countries => {
              const result = countries.filter(
                country =>
                  country.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
              )
              process(result)
            })
          },
        },
        {
          data: 'state.name',
          type: 'autocomplete-objects',
          strict: true,
          source(query: string, process: Function) {
            if (!query || query.length === 0) {
              return
            }

            const columnSettings: any = this
            const row = columnSettings.row
            const countryId = columnSettings.instance.getDataAtRowProp(
              row,
              'country.id'
            )

            if (!countryId) {
              return
            }

            LocationService.getRegions(countryId, query).then(regions =>
              process(regions)
            )
          },
        },
        {
          data: 'city.name',
          type: 'autocomplete-objects',
          strict: true,
          source(this: ColumnSettings, query: string, process: Function) {
            if (!query || query.length === 0) {
              return
            }

            const row = this.row
            const regionId = this.instance.getDataAtRowProp(row, 'state.id')

            LocationService.getPlacesForRegion(regionId, query).then(
              options => {
                return process(options)
              }
            )
          },
        },
        { data: 'minTerm', type: 'numeric' },
        { data: 'maxTerm', type: 'numeric' },
        { data: 'maxWeight', type: 'numeric' },
        { data: 'maxHeight', type: 'numeric' },
        { data: 'maxWidth', type: 'numeric' },
        { data: 'maxLength', type: 'numeric' },
        { data: '' },
      ],
      colWidths: [180, 180, 220, 80, 80, 170, 100, 100, 100],
      stretchH: 'last',
      readOnly: false,
      columnSorting: true,
      minSpareRows: 0,
      manualColumnResize: true,
    }
  },

  getTariffsSettings(tariff: Tariff, zoneNames: any) {
    const zoneIds = Object.keys(zoneNames)

    const headers = zoneIds.map((zoneId: string) => zoneNames[zoneId])
    const columns = zoneIds.map(key => ({
      data: `${key}.rate`,
      type: 'numeric',
    }))

    return {
      colHeaders: ['Вес', ...headers, ''],
      columns: [{ data: 'weight' }, ...columns, { data: '', readonly: true }],
      licenseKey: 'non-commercial-and-evaluation',
      minSpareRows: 0,
      stretchH: 'last',
      readOnly: false,
      columnSorting: true,
      sortIndicator: true,
      height: 'calc(100vh - 224px)',
      manualColumnResize: true,
    }
  },

  getPriceConfigSetting({ currencyCode }: { currencyCode: string }) {
    return {
      colHeaders: [
        `Цена, ${DictionariesService.currencies[currencyCode].display_name}`,
        'Вес, гр',
        '',
      ],
      columns: [
        { data: 'price' },
        { data: 'weight' },
        { data: '', readonly: true },
      ],
      colWidths: [180, 180],
      licenseKey: 'non-commercial-and-evaluation',
      minSpareRows: 0,
      stretchH: 'last',
      readOnly: false,
      columnSorting: true,
      sortIndicator: true,
      height: 'calc(100vh - 224px)',
      manualColumnResize: true,
    }
  },

  getTaxesSettings(currencySign: string = '$') {
    const headers = ['Сумма от', 'Пошлина', 'Сбор', 'Мин.сбор']
    const columns = [
      {
        data: 'tax_from',
        type: 'numeric',
        renderer: this.suffixRenderer(currencySign),
      },
      {
        data: 'tax_percent',
        type: 'numeric',
        renderer: this.suffixRenderer('%'),
      },
      {
        data: 'extra_percent',
        type: 'numeric',
        renderer: this.suffixRenderer('%'),
      },
      {
        data: 'extra_min',
        type: 'numeric',
        renderer: this.suffixRenderer(currencySign),
      },
    ]

    return {
      colHeaders: headers,
      columns: columns,
      licenseKey: 'non-commercial-and-evaluation',
      minSpareRows: 0,
      colWidths: [90, 90, 90, 90],
      stretchH: 'last',
      readOnly: false,
      columnSorting: false,
      sortIndicator: false,
      height: 'calc(45vh - 224px)',
      manualColumnResize: false,
    }
  },

  suffixRenderer(suffix: string = '') {
    return function(
      instance: any,
      td: any,
      row: number,
      col: number,
      prop: string,
      value: number,
      cellProperties: CellProperties
    ) {
      if (value) {
        Handsontable.renderers.NumericRenderer.apply(instance, [
          instance,
          td,
          row,
          col,
          prop,
          value,
          cellProperties,
        ])
        td.innerHTML = `${value}${suffix}`
      }
    }
  },
}
